import { Service, Container } from 'typedi';
import mongoose, { Types, ClientSession, mongo } from 'mongoose';
import { TFunction } from 'i18next';
import { NotificationService } from '../../../Modules/Notification/services/notification.service';
import { RatingDocument } from '../interfaces/rating.interface';
import { HttpException } from 'utils/exceptions/httpException';
import { status } from 'utils/helpers/api.responses';
import Labour from '../../../Modules/Labour/models/labour.model';
import User from '../../../Modules/Auth/models/auth.model';
import Role from '../../../Modules/Role/models/role.model';
import { RolesEnum } from '../../../Modules/Auth/interfaces/auth.interface';
import Rating from '../models/rating.model';
import { aws } from '../../../config/index';

@Service()
export class RatingService {
    public notificationService = Container.get(NotificationService);

    public async createRating(
        userId: string,
        raterId: string,
        rating: number,
        t: TFunction,
        feedback?: string,
        session?: ClientSession
    ): Promise<RatingDocument> {


        session = await mongoose.startSession();
        let transactionCommitted = false

        try {
            session.startTransaction();
            if (!Types.ObjectId.isValid(userId) || !Types.ObjectId.isValid(raterId)) {
                throw new HttpException(status.BadRequest, t('General.invalid', { field: 'ID' }));
            }
            if (!Number.isInteger(rating) || rating < 1 || rating > 5) {
                throw new HttpException(status.BadRequest, t('General.invalid', { field: 'rating' }));
            }
            if (feedback && (typeof feedback !== 'string' || feedback.trim().length > 500)) {
                throw new HttpException(status.BadRequest, t('Rating.invalid_feedback'));
            }
            if (userId === raterId) {
                throw new HttpException(status.BadRequest, t('Rating.self_rating_forbidden'));
            }

            const labourUser = await User.findOne({ _id: userId, isDeleted: false })
                .populate<{ roleId: { _id: Types.ObjectId; name: string } }>('roleId')
                .session(session)
                .lean();
            if (!labourUser) {
                throw new HttpException(status.NotFound, t('General.not_found', { field: t('Labour.labour') }));
            }

            const rater = await User.findOne({ _id: raterId, isDeleted: false })
                .populate('roleId')
                .session(session)
                .lean();
            if (!rater) {
                throw new HttpException(status.NotFound, t('General.not_found', { field: t('User.user') }));
            }

            const adminRole = await Role.findOne({ name: new RegExp(`^${RolesEnum.ADMIN}$`, 'i') })
                .session(session)
                .lean();
            const staffRole = await Role.findOne({ name: new RegExp(`^${RolesEnum.STAFF}$`, 'i') })
                .session(session)
                .lean();

            const isAdminOrStaff =
                (adminRole && (rater.roleId as any)._id.equals(adminRole._id)) ||
                (staffRole && (rater.roleId as any)._id.equals(staffRole._id));

            // const existingRating = await Rating.findOne({
            //     userId,
            //     raterId,
            // })
            //     .session(session)
            //     .lean();
            // if (existingRating) {
            //     throw new HttpException(status.BadRequest, t('Rating.already_exists'));
            // }

            const newRating = new Rating({
                userId,
                raterId,
                rating,
                feedback: feedback?.trim() || null,
            });
            await newRating.save({ session });

            const labour = await Labour.findOne({ userId: userId, isDeleted: false })
                .session(session)
                .lean();
            if (!labour) {
                throw new HttpException(status.NotFound, t('General.not_found', { field: t('Labour.labour') }));
            }
            const ratings = await Rating.find({ userId, isDeleted: false }).session(session).lean();
            const totalRatings = ratings.length;
            const sumRatings = ratings.reduce((sum, r) => sum + r.rating, 0);
            const averageRating = sumRatings / totalRatings

            await Labour.updateOne(
                { userId: userId },
                { $set: { averageRating: Number(averageRating.toFixed(1)), ratingCount: totalRatings } },
                { session }
            );
            await session.commitTransaction();
            transactionCommitted = true

            // await this.notificationService.sendNotification(
            //     'labourManagement',
            //     'newRatingReceived',
            //     {
            //         from: new Types.ObjectId(raterId),
            //         to: [new Types.ObjectId(labourUser._id)],
            //         entityData: {
            //             userId,
            //             rating,
            //             feedback: feedback?.trim() || null,
            //             raterId,
            //             firstName: rater.firstName,
            //             lastName: rater.lastName,
            //             raterName: rater?.firstName ? `${rater.firstName} ${rater.lastName || ''}`.trim() : rater?.email || 'Unknown',
            //             adminName: rater?.firstName ? `${rater.firstName} ${rater.lastName || ''}`.trim() : rater?.email || 'Admin',
            //             labourId: labour?.employee_id || 'N/A',

            //         },
            //     },
            //     t
            // );

            return newRating;
        } catch (error) {
            console.log(error)
            if (!transactionCommitted) {
                await session.abortTransaction();
            }
            throw error instanceof HttpException
                ? error
                : new HttpException(status.InternalServerError, t('General.errorCreating', { field: 'rating' }));
        } finally {
            if (!session.inTransaction()) session.endSession();
        }
    }

    public async getAllRatings({
        filters,
        page = 1,
        limit = 10,
        search = "",
        sortBy = "createdAt",
        sortOrder = "desc",
        t,
    }: {
        filters: { userId?: string;[key: string]: any };
        page: number;
        limit: number;
        search: string;
        sortBy: string;
        sortOrder: string;
        t: TFunction;
    }): Promise<{ data: RatingDocument[]; total: number; page: number; totalPages: number }> {
        try {
            Object.keys(filters).forEach(key => {
                if (filters[key] === undefined) {
                    delete filters[key];
                }
            });
            const query: any = { isDeleted: false, ...filters };

            if (search) {
                query.feedback = { $regex: search, $options: "i" };
            }

            let sortOption: any = {};
            if (sortBy) {
                sortOption[sortBy] = sortOrder === "desc" ? -1 : 1;
            }

            const skip = (page - 1) * limit;

            const [ratings, total] = await Promise.all([
                Rating.find(query)
                    .populate({
                        path: "userId",
                        select: "firstName lastName email profileImage",
                        populate: {
                            path: "profileImage",
                            select: "filename key",
                        },
                    })
                    .populate({
                        path: "raterId",
                        select: "firstName lastName email profileImage",
                        populate: {
                            path: "profileImage",
                            select: "filename key",
                        },
                    })

                    .sort(sortOption)
                    .skip(skip)
                    .limit(limit)
                    .lean(),
                Rating.countDocuments(query),
            ]);

            const formattedRatings = ratings.map((rating) => ({
                _id: rating._id,
                rating: rating.rating,
                feedback: rating.feedback,
                isDeleted: rating.isDeleted,
                deletedAt: rating.deletedAt,
                createdAt: rating.createdAt,
                updatedAt: rating.updatedAt,
                labour: rating.userId
                    ? {
                        firstName: (rating.userId as any).firstName,
                        lastName: (rating.userId as any).lastName,
                        email: (rating.userId as any).email,
                        userId: (rating.userId as any)._id,
                        profileImage: (rating.userId as any).profileImage &&
                            "filename" in (rating.userId as any).profileImage &&
                            "key" in (rating.userId as any).profileImage
                            ? {
                                _id: (rating.userId as any).profileImage._id.toString(),
                                filename: ((rating.userId as any).profileImage as { filename: string }).filename,
                                url: `${aws.S3_BASE_URL}/${(rating.userId as any).profileImage.key}`,
                            }
                            : null,
                    }
                    : undefined,
                rater: rating.raterId
                    ? {
                        firstName: (rating.raterId as any).firstName,
                        lastName: (rating.raterId as any).lastName,
                        email: (rating.raterId as any).email,
                        userId: (rating.raterId as any)._id,
                        profileImage: (rating.raterId as any).profileImage &&
                            "filename" in (rating.raterId as any).profileImage &&
                            "key" in (rating.raterId as any).profileImage
                            ? {
                                _id: (rating.raterId as any).profileImage._id.toString(),
                                filename: ((rating.raterId as any).profileImage as { filename: string }).filename,
                                url: `${aws.S3_BASE_URL}/${(rating.raterId as any).profileImage.key}`,
                            }
                            : null,
                    }
                    : undefined,
            }));

            return {
                data: formattedRatings as any,
                total,
                page,
                totalPages: Math.ceil(total / limit),
            };
        } catch (error) {
            console.log(error)
            throw error instanceof HttpException
                ? error
                : new HttpException(status.InternalServerError, t("General.errorFetching", { field: t("Rating.ratings") }));
        }
    }

    public async getRatingByUserId({
        userId,
        t,
    }: {
        userId: string;
        t: TFunction;
    }): Promise<RatingDocument[]> {
        try {
            const ratings = await Rating.find({
                userId,
                isDeleted: false,
            })
                .populate({
                    path: "userId",
                    select: "firstName lastName email profileImage",
                    populate: {
                        path: "profileImage",
                        select: "filename key",
                    },
                })
                .populate({
                    path: "raterId",
                    select: "firstName lastName email profileImage",
                    populate: {
                        path: "profileImage",
                        select: "filename key",
                    },
                })

                .sort({ createdAt: -1 })
                .lean();

            const formattedRatings = ratings.map((rating) => ({
                _id: rating._id,
                rating: rating.rating,
                feedback: rating.feedback,
                isDeleted: rating.isDeleted,
                deletedAt: rating.deletedAt,
                createdAt: rating.createdAt,
                updatedAt: rating.updatedAt,
                labour: rating.userId
                    ? {
                        firstName: (rating.userId as any).firstName,
                        lastName: (rating.userId as any).lastName,
                        email: (rating.userId as any).email,
                        userId: (rating.userId as any)._id,
                        profileImage: (rating.userId as any).profileImage &&
                            "filename" in (rating.userId as any).profileImage &&
                            "key" in (rating.userId as any).profileImage
                            ? {
                                _id: (rating.userId as any).profileImage._id.toString(),
                                filename: (rating.userId as any).profileImage.filename,
                                url: `${aws.S3_BASE_URL}/${(rating.userId as any).profileImage.key}`,
                            }
                            : null,
                    }
                    : undefined,
                rater: rating.raterId
                    ? {
                        firstName: (rating.raterId as any).firstName,
                        lastName: (rating.raterId as any).lastName,
                        email: (rating.raterId as any).email,
                        userId: (rating.raterId as any)._id,
                        profileImage: (rating.raterId as any).profileImage &&
                            "filename" in (rating.raterId as any).profileImage &&
                            "key" in (rating.raterId as any).profileImage
                            ? {
                                _id: (rating.raterId as any).profileImage._id.toString(),
                                filename: (rating.raterId as any).profileImage.filename,
                                url: `${aws.S3_BASE_URL}/${(rating.raterId as any).profileImage.key}`,
                            }
                            : null,
                    }
                    : undefined,
            }));

            return formattedRatings as any;
        } catch (error) {
            throw error instanceof HttpException
                ? error
                : new HttpException(
                    status.InternalServerError,
                    t("General.errorFetching", { field: t("Rating.ratings") })
                );
        }
    }


    public async getRatingById(id: string, userId: string, t: TFunction): Promise<RatingDocument> {
        try {
            if (!Types.ObjectId.isValid(id)) {
                throw new HttpException(status.BadRequest, t('General.invalid', { field: 'ID' }));
            }

            const rating = await Rating.findOne({ _id: id, isDeleted: false })
                .populate({
                    path: "userId",
                    select: "firstName lastName email profileImage",
                    populate: {
                        path: "profileImage",
                        select: "filename key",
                    },
                })
                .populate({
                    path: "raterId",
                    select: "firstName lastName email profileImage",
                    populate: {
                        path: "profileImage",
                        select: "filename key",
                    },
                })
                .lean();
            if (!rating) {
                throw new HttpException(status.NotFound, t('General.not_found', { field: t('Rating.ratings') }));
            }

            const user = await User.findOne({ _id: userId, isDeleted: false })
                .populate<{ roleId: { name: string } }>('roleId')
                .lean();

            if (!user) {
                throw new HttpException(status.NotFound, t('General.not_found', { field: t('User.user') }));
            }

            if (user.roleId.name.toLowerCase() === RolesEnum.LABOUR.toLowerCase()) {
                if (!(rating.userId as any)._id.equals(user._id)) {
                    throw new HttpException(status.Forbidden, t('Rating.view_own_only'));
                }
            }

            return {
                _id: rating._id,
                rating: rating.rating,
                feedback: rating.feedback,
                isDeleted: rating.isDeleted,
                deletedAt: rating.deletedAt,
                createdAt: rating.createdAt,
                updatedAt: rating.updatedAt,
                labour: rating.userId
                    ? {
                        firstName: (rating.userId as any).firstName,
                        lastName: (rating.userId as any).lastName,
                        email: (rating.userId as any).email,
                        userId: (rating.userId as any)._id,
                        profileImage: (rating.userId as any).profileImage &&
                            'filename' in (rating.userId as any).profileImage &&
                            'key' in (rating.userId as any).profileImage
                            ? {
                                _id: (rating.userId as any).profileImage._id.toString(),
                                filename: ((rating.userId as any).profileImage as { filename: string }).filename,
                                url: `${aws.S3_BASE_URL}/${(rating.userId as any).profileImage.key}`,
                            }
                            : null,
                    }
                    : undefined,
                rater: rating.raterId
                    ? {
                        firstName: (rating.raterId as any).firstName,
                        lastName: (rating.raterId as any).lastName,
                        email: (rating.raterId as any).email,
                        userId: (rating.raterId as any)._id,
                        profileImage: (rating.raterId as any).profileImage &&
                            'filename' in (rating.raterId as any).profileImage &&
                            'key' in (rating.raterId as any).profileImage
                            ? {
                                _id: (rating.raterId as any).profileImage._id.toString(),
                                filename: ((rating.raterId as any).profileImage as { filename: string }).filename,
                                url: `${aws.S3_BASE_URL}/${(rating.raterId as any).profileImage.key}`,
                            }
                            : null,
                    }
                    : undefined,
            } as any;
        } catch (error) {
            console.log(error);
            throw error instanceof HttpException
                ? error
                : new HttpException(status.InternalServerError, t('General.errorFetching', { field: t('Rating.ratings') }));
        }
    }

    public async updateRating(
        id: string,
        updates: { rating?: number; feedback?: string },
        updatedBy: string,
        t: TFunction,
        session?: ClientSession
    ): Promise<RatingDocument> {


        session = await mongoose.startSession();
        let transactionCommitted = false
        try {
            session.startTransaction();
            if (!Types.ObjectId.isValid(id) || !Types.ObjectId.isValid(updatedBy)) {
                throw new HttpException(status.BadRequest, t('General.invalid', { field: 'ID' }));
            }

            const rating = await Rating.findOne({ _id: id, isDeleted: false })
                .populate({
                    path: "userId",
                    select: "firstName lastName email profileImage",
                    populate: {
                        path: "profileImage",
                        select: "filename key",
                    },
                })
                .populate({
                    path: "raterId",
                    select: "firstName lastName email profileImage",
                    populate: {
                        path: "profileImage",
                        select: "filename key",
                    },
                })

                .session(session);
            if (!rating) {
                throw new HttpException(status.NotFound, t('General.not_found', { field: t('Rating.ratings') }));
            }
            const rater = await User.findOne({ _id: rating.raterId, isDeleted: false })

            const updatedByUser = await User.findOne({ _id: updatedBy, isDeleted: false })
                .populate('roleId')
                .session(session)
                .lean();
            if (!updatedByUser) {
                throw new HttpException(status.NotFound, t('General.not_found', { field: t('User.user') }));
            }

            const adminRole = await Role.findOne({ name: new RegExp(`^${RolesEnum.ADMIN}$`, 'i') })
                .session(session)
                .lean();
            const staffRole = await Role.findOne({ name: new RegExp(`^${RolesEnum.STAFF}$`, 'i') })
                .session(session)
                .lean();
            const isAdminOrStaff =
                (adminRole && (updatedByUser.roleId as any)._id.equals(adminRole._id)) ||
                (staffRole && (updatedByUser.roleId as any)._id.equals(staffRole._id));
            if (!isAdminOrStaff) {
                throw new HttpException(status.Forbidden, t('Role.permission_denied'));
            }

            if (updates.rating && (!Number.isInteger(updates.rating) || updates.rating < 1 || updates.rating > 5)) {
                throw new HttpException(status.BadRequest, t('General.invalid', { field: 'rating' }));
            }
            if (updates.feedback && (typeof updates.feedback !== 'string' || updates.feedback.trim().length > 500)) {
                throw new HttpException(status.BadRequest, t('Rating.invalid_feedback'));
            }

            const updateData: Partial<RatingDocument> = {
                rating: updates.rating ?? rating.rating,
                feedback: updates.feedback ? updates.feedback.trim() : rating.feedback,
                updatedAt: new Date(),
            };

            Object.assign(rating, updateData);
            await rating.save({ session });

            const labour = await Labour.findOne({ userId: rating.userId, isDeleted: false })
                .session(session)
                .lean();
            if (!labour) {
                throw new HttpException(status.NotFound, t('General.not_found', { field: t('Labour.labour') }));
            }

            const ratings = await Rating.find({ userId: rating.userId, isDeleted: false }).session(session).lean();
            const totalRatings = ratings.length;
            const sumRatings = ratings.reduce((sum, r) => sum + r.rating, 0);
            const averageRating = sumRatings / totalRatings

            await Labour.updateOne(
                { userId: rating.userId },
                { $set: { averageRating: Number(averageRating.toFixed(1)), ratingCount: totalRatings } },
                { session }
            );
            await session.commitTransaction();
            transactionCommitted = true

            // await this.notificationService.sendNotification(
            //     'labourManagement',
            //     'ratingUpdated',
            //     {
            //         from: new Types.ObjectId(updatedBy),
            //         to: [rating.userId as any],
            //         entityData: {
            //             ratingId: rating._id,
            //             userId: rating.userId,
            //             rating: rating.rating,
            //             feedback: rating.feedback,
            //             updatedBy,
            //             updatedFields: Object.keys(updates).join(', '),
            //             firstName: rating.userId.firstName,
            //             lastName: rating.userId.lastName,
            //             labourId: labour.employee_id,
            //             adminName: rater?.firstName ? `${rater.firstName} ${rater.lastName || ''}`.trim() : rater?.email || 'Admin',
            //             raterName: rater?.firstName ? `${rater.firstName} ${rater.lastName || ''}`.trim() : rater?.email || 'Unknown',

            //         },
            //     },
            //     t
            // );


            return {
                _id: rating._id,
                rating: rating.rating,
                feedback: rating.feedback,
                isDeleted: rating.isDeleted,
                deletedAt: rating.deletedAt,
                createdAt: rating.createdAt,
                updatedAt: rating.updatedAt,
                labour: rating.userId
                    ? {
                        firstName: (rating.userId as any).firstName,
                        lastName: (rating.userId as any).lastName,
                        email: (rating.userId as any).email,
                        userId: (rating.userId as any)._id,
                        profileImage: (rating.userId as any).profileImage &&
                            'filename' in (rating.userId as any).profileImage &&
                            'key' in (rating.userId as any).profileImage
                            ? {
                                _id: (rating.userId as any).profileImage._id.toString(),
                                filename: ((rating.userId as any).profileImage as { filename: string }).filename,
                                url: `${aws.S3_BASE_URL}/${(rating.userId as any).profileImage.key}`,
                            }
                            : null,
                    }
                    : undefined,
                rater: rating.raterId
                    ? {
                        firstName: (rating.raterId as any).firstName,
                        lastName: (rating.raterId as any).lastName,
                        email: (rating.raterId as any).email,
                        userId: (rating.raterId as any)._id,
                        profileImage: (rating.raterId as any).profileImage &&
                            'filename' in (rating.raterId as any).profileImage &&
                            'key' in (rating.raterId as any).profileImage
                            ? {
                                _id: (rating.raterId as any).profileImage._id.toString(),
                                filename: ((rating.raterId as any).profileImage as { filename: string }).filename,
                                url: `${aws.S3_BASE_URL}/${(rating.raterId as any).profileImage.key}`,
                            }
                            : null,
                    }
                    : undefined,
            } as any;
        } catch (error) {
            if (!transactionCommitted) {
                await session.abortTransaction();
            }
            throw error instanceof HttpException
                ? error
                : new HttpException(status.InternalServerError, t('General.errorUpdating', { field: t('Rating.ratings') }));
        } finally {
            if (!session.inTransaction()) session.endSession();
        }
    }

    public async deleteMultipleRatings(ids: string[], deletedBy: string, t: TFunction): Promise<{ deleted: number }> {
        const session = await mongoose.startSession();
        let transactionCommitted = false
        try {
            session.startTransaction();

            if (!ids.every(id => Types.ObjectId.isValid(id))) {
                throw new HttpException(status.BadRequest, t('General.invalid', { field: 'IDs' }));
            }

            const ratings = await Rating.find({ _id: { $in: ids }, isDeleted: false })
                .populate<{ userId: { _id: Types.ObjectId; firstName: string; lastName: string; email: string; profileImage: any } }>(
                    'userId',
                    'firstName lastName email profileImage'
                )
                .populate<{ raterId: { _id: Types.ObjectId; firstName: string; lastName: string; email: string; profileImage: any } }>(
                    'raterId',
                    'firstName lastName email profileImage'
                )
                .session(session);
            if (!ratings.length) {
                await session.commitTransaction();
                return { deleted: 0 };
            }

            const deletedByUser = await User.findOne({ _id: deletedBy, isDeleted: false })
                .populate<{ roleId: { _id: Types.ObjectId; name: string } }>('roleId')
                .session(session)
                .lean();
            if (!deletedByUser) {
                throw new HttpException(status.NotFound, t('General.not_found', { field: t('User.user') }));
            }

            const adminRole = await Role.findOne({ name: new RegExp(`^${RolesEnum.ADMIN}$`, 'i') })
                .session(session)
                .lean();
            const staffRole = await Role.findOne({ name: new RegExp(`^${RolesEnum.STAFF}$`, 'i') })
                .session(session)
                .lean();
            const isAdminOrStaff =
                (adminRole && (deletedByUser.roleId._id as mongoose.Types.ObjectId).equals(adminRole._id as mongoose.Types.ObjectId)) ||
                (staffRole && (deletedByUser.roleId._id as mongoose.Types.ObjectId).equals(staffRole._id as mongoose.Types.ObjectId));
            if (!isAdminOrStaff) {
                throw new HttpException(status.Forbidden, t('Role.permission_denied'));
            }

            const bulkOps = ratings.map(rating => ({
                updateOne: {
                    filter: { _id: rating._id },
                    update: {
                        $set: {
                            isDeleted: true,
                            deletedAt: new Date(),
                            updatedAt: new Date(),
                        },
                    },
                },
            }));
            await Rating.bulkWrite(bulkOps, { session });

            const userIds = [...new Set(ratings.map(r => r.userId._id.toString()))];
            for (const userId of userIds) {
                const labour = await Labour.findOne({ userId: new Types.ObjectId(userId), isDeleted: false })
                    .session(session)
                    .lean();
                if (!labour) continue;
                const labourRatings = await Rating.find({ userId: new Types.ObjectId(userId), isDeleted: false })
                    .session(session)
                    .lean();
                const totalRatings = labourRatings.length;
                const sumRatings = labourRatings.reduce((sum, r) => sum + r.rating, 0);
                const averageRating = sumRatings / totalRatings

                await Labour.updateOne(
                    { userId: new Types.ObjectId(userId) },
                    { $set: { averageRating: Number(averageRating.toFixed(1)), ratingCount: totalRatings } },
                    { session }
                );
            }
            const labours = await Labour.find({ userId: { $in: userIds }, isDeleted: false })
                .select('userId employee_id')
                .session(session)
                .lean();
            const labourMap = new Map(labours.map(l => [l.userId.toString(), l.employee_id]));

            await session.commitTransaction();
            transactionCommitted = true
            // await Promise.all(
            //     ratings.map(rating =>
            //         this.notificationService.sendNotification(
            //             'labourManagement',
            //             'ratingDeleted',
            //             {
            //                 from: new Types.ObjectId(deletedBy),
            //                 to: [rating.userId as any],
            //                 entityData: {
            //                     ratingId: rating._id,
            //                     userId: rating.userId._id,
            //                     rating: rating.rating,
            //                     feedback: rating.feedback,
            //                     deletedBy,
            //                     firstName: rating.userId.firstName,
            //                     lastName: rating.userId.lastName,
            //                     raterName: rating.raterId?.firstName ? `${rating.raterId.firstName} ${rating.raterId.lastName || ''}`.trim() : rating.raterId?.email || 'Unknown',
            //                     adminName: rating.raterId?.firstName ? `${rating.raterId.firstName} ${rating.raterId.lastName || ''}`.trim() : rating.raterId?.email || 'Admin',
            //                     labourId: labourMap.get(rating.userId._id.toString()) || '',
            //                 },
            //             },
            //             t
            //         )
            //     )
            // );

            return { deleted: ratings.length };
        } catch (error) {
            if (!transactionCommitted) await session.abortTransaction();
            throw error instanceof HttpException
                ? error
                : new HttpException(status.InternalServerError, t('General.errorDeleting', { field: t('Rating.ratings') }));
        } finally {
            if (!session.inTransaction()) session.endSession();
        }
    }
}